import React, { useEffect, useState, useCallback } from 'react';
import {
  Crown, Shield, Plus, Edit2, Trash2, Save, X,
  Loader2, AlertCircle, CheckCircle2, MapPin, Phone, Mail
} from 'lucide-react';

interface Member {
  id:string; name:string; role:string; village?:string; title?:string;
  bio?:string; phone?:string; email?:string; occupation?:string;
  organization?:string; state?:string; photoUrl?:string;
  sortOrder?:number; isActive?:boolean;
}
const COMMITTEE_ROLES = [
  'Chairman','Vice-Chairman','Secretary-General','Assistant Secretary',
  'Financial Secretary','Publicity Secretary','Social Secretary',
  'Welfare Officer','Legal Adviser','Ex-officio Member','Member'
];
const TRUSTEE_ROLES = [
  'Board Chairman','Vice-Chairman','Trustee',
  'Honorary Trustee','Legal Adviser','Financial Adviser'
];
type Panel = 'committee'|'trustees';
interface GovernanceAdminProps { token:string; }

export default function GovernanceAdmin({ token }:GovernanceAdminProps) {
  const [panel,    setPanel]    = useState<Panel>('committee');
  const [members,  setMembers]  = useState<Member[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState<{type:'ok'|'err';text:string}|null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState<Member|null>(null);

  const apiPath = panel==='committee'
    ? '/api/admin/management-committee'
    : '/api/admin/board-of-trustees';
  const roles = panel==='committee' ? COMMITTEE_ROLES : TRUSTEE_ROLES;
  const blank:Partial<Member> = {
    name:'',role:roles[0],village:'',title:'',bio:'',phone:'',email:'',
    photoUrl:'',sortOrder:99,isActive:true,occupation:'',organization:'',state:''
  };
  const [form,setForm] = useState<Partial<Member>>(blank);

  const flash = (type:'ok'|'err',text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),4000);
  };
  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const r = await fetch(apiPath,{headers:{Authorization:`Bearer ${token}`}});
      const d = await r.json();
      setMembers(d.members||[]);
    } catch { flash('err','Failed to load'); }
    finally { setLoading(false); }
  },[apiPath,token]);

  useEffect(()=>{ load(); setShowForm(false); setEditing(null); },[panel]);

  const openAdd  = ()=>{ setEditing(null); setForm(blank); setShowForm(true); };
  const openEdit = (m:Member)=>{ setEditing(m); setForm({...m}); setShowForm(true); };
  const cancel   = ()=>{ setShowForm(false); setEditing(null); setForm(blank); };

  const save = async()=>{
    if (!form.name?.trim()||!form.role) return flash('err','Name and role are required');
    setSaving(true);
    try {
      const url    = editing?`${apiPath}/${editing.id}`:apiPath;
      const method = editing?'PUT':'POST';
      const r = await fetch(url,{
        method, headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},
        body:JSON.stringify(form)
      });
      if (!r.ok) throw new Error((await r.json()).error||'Save failed');
      flash('ok',editing?'Updated successfully':'Member added successfully');
      cancel(); load();
    } catch(e:any){ flash('err',e.message); }
    finally { setSaving(false); }
  };

  const remove = async(m:Member)=>{
    if (!confirm(`Remove ${m.name}?`)) return;
    try {
      const r = await fetch(`${apiPath}/${m.id}`,{
        method:'DELETE',headers:{Authorization:`Bearer ${token}`}
      });
      if (!r.ok) throw new Error('Delete failed');
      flash('ok',`${m.name} removed`); load();
    } catch(e:any){ flash('err',e.message); }
  };

  const isCommittee = panel==='committee';

  return (
    <div className="bg-[#070D0A] min-h-screen p-4 md:p-6">
      <div className="max-w-5xl mx-auto">

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-cinzel text-2xl text-[#EDE8DC]">Governance Admin</h1>
            <p className="text-[#EDE8DC]/50 text-sm mt-0.5">
              Manage committee and trustee profiles
            </p>
          </div>
          <button onClick={openAdd}
                  className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#E2C97E]
                             text-[#070D0A] font-semibold px-4 py-2 rounded-lg
                             transition-colors text-sm">
            <Plus className="w-4 h-4"/> Add Member
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          {(['committee','trustees'] as Panel[]).map(p=>(
            <button key={p} onClick={()=>setPanel(p)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl
                                font-semibold text-sm transition-all ${panel===p
                      ?'bg-[#C9A84C] text-[#070D0A]'
                      :'bg-[#0E1812] border border-[#1C2E22] text-[#EDE8DC]/70 hover:border-[#C9A84C]/40'
                    }`}>
              {p==='committee'
                ?<><Crown className="w-4 h-4"/>Management Committee</>
                :<><Shield className="w-4 h-4"/>Board of Trustees</>}
            </button>
          ))}
        </div>

        {msg && (
          <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 text-sm
                           ${msg.type==='ok'
                             ?'bg-green-900/30 border border-green-500/30 text-green-400'
                             :'bg-red-900/30 border border-red-500/30 text-red-400'}`}>
            {msg.type==='ok'
              ?<CheckCircle2 className="w-4 h-4 flex-shrink-0"/>
              :<AlertCircle  className="w-4 h-4 flex-shrink-0"/>}
            {msg.text}
          </div>
        )}

        {showForm && (
          <div className="bg-[#0E1812] border border-[#C9A84C]/30 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[#C9A84C] font-cinzel font-semibold">
                {editing?'Edit Member':'Add Member'}
              </h2>
              <button onClick={cancel}><X className="w-5 h-5 text-[#EDE8DC]/50"/></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Full Name *</label>
                <input value={form.name||''}
                       onChange={e=>setForm(p=>({...p,name:e.target.value}))}
                       placeholder="Enter full name"
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Role *</label>
                <select value={form.role||roles[0]}
                        onChange={e=>setForm(p=>({...p,role:e.target.value}))}
                        className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                   px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                   focus:border-[#C9A84C]/50">
                  {roles.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">
                  Honorific / Title
                </label>
                <input value={form.title||''}
                       onChange={e=>setForm(p=>({...p,title:e.target.value}))}
                       placeholder="e.g. Chief, Dr., Engr., Hon."
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">
                  {isCommittee?'Village / Community':'State of Origin'}
                </label>
                <input
                  value={isCommittee?(form.village||''):(form.state||'')}
                  onChange={e=>setForm(p=>isCommittee
                    ?({...p,village:e.target.value})
                    :({...p,state:e.target.value}))}
                  placeholder={isCommittee?'e.g. Jattu-Uzairue':'e.g. Edo State'}
                  className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                             px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                             focus:border-[#C9A84C]/50"/>
              </div>
              {!isCommittee && <>
                <div>
                  <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Occupation</label>
                  <input value={form.occupation||''}
                         onChange={e=>setForm(p=>({...p,occupation:e.target.value}))}
                         placeholder="e.g. Engineer, Lawyer, Businessman"
                         className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                    px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                    focus:border-[#C9A84C]/50"/>
                </div>
                <div>
                  <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Organization</label>
                  <input value={form.organization||''}
                         onChange={e=>setForm(p=>({...p,organization:e.target.value}))}
                         placeholder="e.g. Uzairue Development Union"
                         className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                    px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                    focus:border-[#C9A84C]/50"/>
                </div>
              </>}
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Phone</label>
                <input value={form.phone||''}
                       onChange={e=>setForm(p=>({...p,phone:e.target.value}))}
                       placeholder="+234 ..."
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Email</label>
                <input value={form.email||''} type="email"
                       onChange={e=>setForm(p=>({...p,email:e.target.value}))}
                       placeholder="email@example.com"
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">
                  Photo URL (Cloudinary)
                </label>
                <input value={form.photoUrl||''}
                       onChange={e=>setForm(p=>({...p,photoUrl:e.target.value}))}
                       placeholder="https://res.cloudinary.com/uuovmrek/image/upload/..."
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">Biography</label>
                <textarea value={form.bio||''}
                          onChange={e=>setForm(p=>({...p,bio:e.target.value}))}
                          rows={3} placeholder="Brief biography..."
                          className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                     px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                     focus:border-[#C9A84C]/50 resize-none"/>
              </div>
              <div>
                <label className="text-[#EDE8DC]/70 text-xs mb-1 block">
                  Sort Order (1=first)
                </label>
                <input value={form.sortOrder??99} type="number" min={1} max={99}
                       onChange={e=>setForm(p=>({...p,sortOrder:Number(e.target.value)}))}
                       className="w-full bg-[#121E16] border border-[#1C2E22] rounded-lg
                                  px-3 py-2 text-[#EDE8DC] text-sm focus:outline-none
                                  focus:border-[#C9A84C]/50"/>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isActive!==false}
                         onChange={e=>setForm(p=>({...p,isActive:e.target.checked}))}
                         className="w-4 h-4 accent-[#C9A84C]"/>
                  <span className="text-[#EDE8DC]/70 text-sm">
                    Active (visible to public)
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#1C2E22]">
              <button onClick={cancel}
                      className="px-5 py-2 rounded-lg border border-[#1C2E22]
                                 text-[#EDE8DC]/70 text-sm hover:border-[#C9A84C]/30">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                      className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#E2C97E]
                                 text-[#070D0A] font-semibold px-6 py-2 rounded-lg
                                 transition-colors text-sm disabled:opacity-50">
                {saving?<Loader2 className="w-4 h-4 animate-spin"/>
                       :<Save className="w-4 h-4"/>}
                {editing?'Update Member':'Add Member'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#C9A84C] animate-spin"/>
          </div>
        ) : members.length===0 ? (
          <div className="text-center py-20 text-[#EDE8DC]/40">
            {isCommittee
              ?<Crown  className="w-12 h-12 mx-auto mb-3 opacity-30"/>
              :<Shield className="w-12 h-12 mx-auto mb-3 opacity-30"/>}
            <p className="mb-4">No members yet.</p>
            <button onClick={openAdd}
                    className="bg-[#C9A84C]/20 border border-[#C9A84C]/30
                               text-[#C9A84C] px-6 py-2 rounded-xl text-sm
                               hover:bg-[#C9A84C]/30 transition-colors">
              + Add First Member
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map(m=>(
              <div key={m.id}
                   className="bg-[#0E1812] border border-[#1C2E22] rounded-xl p-4
                              flex items-center gap-4
                              hover:border-[#C9A84C]/20 transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#121E16] border border-[#1C2E22]
                                flex-shrink-0 overflow-hidden">
                  {m.photoUrl
                    ?<img src={m.photoUrl} alt={m.name} className="w-full h-full object-cover"/>
                    :<div className="w-full h-full flex items-center justify-center">
                       <span className="text-[#C9A84C] font-bold">{m.name.charAt(0)}</span>
                     </div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#EDE8DC] font-semibold text-sm">
                      {m.title?`${m.title} `:''}{m.name}
                    </span>
                    {!m.isActive && (
                      <span className="text-xs bg-[#1C2E22] text-[#EDE8DC]/40
                                       rounded-full px-2 py-0.5">inactive</span>
                    )}
                  </div>
                  <p className="text-[#C9A84C] text-xs mt-0.5">{m.role}</p>
                  <p className="text-[#EDE8DC]/40 text-xs">
                    {m.village||m.state||m.occupation||''}
                  </p>
                </div>
                <span className="text-[#EDE8DC]/20 text-xs flex-shrink-0">
                  #{m.sortOrder||99}
                </span>
                <div className="flex gap-2">
                  <button onClick={()=>openEdit(m)}
                          className="p-2 rounded-lg bg-[#121E16] border border-[#1C2E22]
                                     text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors">
                    <Edit2 className="w-4 h-4"/>
                  </button>
                  <button onClick={()=>remove(m)}
                          className="p-2 rounded-lg bg-[#121E16] border border-[#1C2E22]
                                     text-red-400 hover:border-red-500/40 transition-colors">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
